import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Wrench, Truck, Package, FileText, Stethoscope } from "lucide-react";

// Each transition button maps current_status -> next_status with a label.
const TRANSITIONS = [
  { from: "assigned", to: "received", label: "Enhet mottatt" },
  { from: "received", to: "in_progress", label: "Reparasjon påbegynt" },
  { from: "in_progress", to: "repair_done", label: "Reparasjon fullført" },
  { from: "repair_done", to: "sent_back", label: "Sendt tilbake" },
];

const STATUS_LABEL = {
  assigned: "Betalt — venter på enhet",
  received: "Enhet mottatt",
  in_progress: "Under reparasjon",
  repair_done: "Reparasjon fullført",
  sent_back: "Sendt tilbake",
  completed: "Fullført",
  diagnosis_ready: "Diagnose sendt til kunde",
  approved_awaiting_extra_payment: "Kunde godkjent — venter på betaling",
  declined_returning: "Avslått",
};

export default function FixerJobDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [diag, setDiag] = useState({ findings: "", recommended_repair: "", recommended_price: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setOrder(null));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advance = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/fixer/orders/${id}/status`, { status: next });
      toast.success(`Status oppdatert: ${STATUS_LABEL[next]}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitDiagnosis = async () => {
    if (!diag.findings || !diag.recommended_repair || !diag.recommended_price) {
      toast.error("Fyll ut funn, anbefalt reparasjon og pris.");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/fixer/orders/${id}/diagnosis`, {
        findings: diag.findings,
        recommended_repair: diag.recommended_repair,
        recommended_price: parseFloat(diag.recommended_price),
      });
      toast.success("Diagnose sendt til kunde");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!order) return <div className="tk-container py-20 text-slate-500">Laster …</div>;

  const transition = TRANSITIONS.find((t) => t.from === order.status);
  const isDiagnosticAwaitingFindings = order.is_diagnostic && order.status === "assigned";

  return (
    <div className="tk-container py-16 max-w-3xl" data-testid="fixer-job-detail">
      <Link to="/verksted-dashbord" className="text-sm text-slate-500 hover:text-[#0B1C30]">
        ← Tilbake til dashbord
      </Link>

      <div className="mt-6 flex items-center gap-2 mb-2">
        <p className="tk-overline font-mono">#{order.id.slice(0, 8)}</p>
        {order.is_diagnostic && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#4A7C59]/10 text-[#4A7C59]">
            <Stethoscope size={10} /> Diagnose
          </span>
        )}
      </div>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">{order.product_name}</h1>
      <p className="text-sm text-slate-500 mb-10">
        Service: {order.service_name} · Status: {STATUS_LABEL[order.status] || order.status}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><Wrench size={14} /> Reparasjon</p>
          <p className="text-sm font-medium">{order.service_name}</p>
          {order.is_diagnostic && order.symptoms?.length > 0 && (
            <p className="text-xs text-slate-500 mt-2">Symptomer: {order.symptoms.join(", ")}</p>
          )}
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><Truck size={14} /> Levering</p>
          <p className="text-sm">{order.delivery_method === "shipping" ? "Bring-frakt" : "Selv-levering (drop-off)"}</p>
          {order.shipping_label_url && (
            <a href={order.shipping_label_url} target="_blank" rel="noreferrer" className="text-xs text-[#0B1C30] underline mt-2 inline-block">
              Last ned fraktetikett →
            </a>
          )}
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3 flex items-center gap-2"><Package size={14} /> Del fra Tekkdeler</p>
          <p className="text-sm">{order.product_name}</p>
          {order.is_diagnostic ? (
            <p className="text-xs text-slate-500 mt-1">Diagnose – ingen del bestilt på forhånd</p>
          ) : (
            <p className="text-xs text-slate-500 mt-1">Del er klargjort av Tekkdeler</p>
          )}
        </div>
        <div className="tk-card">
          <p className="tk-overline mb-3">Arbeid (din andel)</p>
          <p className="text-xl font-semibold">kr {Number(order.service_fee || 0).toFixed(0)}</p>
        </div>
      </div>

      {/* Privacy note */}
      <div className="text-xs text-slate-400 mb-8 px-3">
        Av personvernhensyn vises ikke kundens kontaktopplysninger.
      </div>

      {/* Diagnostic findings form */}
      {isDiagnosticAwaitingFindings && (
        <div className="tk-card mb-6" data-testid="diagnostic-findings-form">
          <p className="tk-overline mb-3 flex items-center gap-2"><FileText size={14} /> Fyll inn diagnose</p>
          <p className="text-xs text-slate-500 mb-4">Når du sender inn diagnosen, mottar kunden e-post med godkjenn/avslå.</p>
          <textarea
            placeholder="Funn (hva er galt med enheten?)"
            className="tk-input min-h-[90px] mb-3"
            value={diag.findings}
            onChange={(e) => setDiag({ ...diag, findings: e.target.value })}
            data-testid="job-diag-findings"
          />
          <input
            placeholder="Anbefalt reparasjon"
            className="tk-input mb-3"
            value={diag.recommended_repair}
            onChange={(e) => setDiag({ ...diag, recommended_repair: e.target.value })}
            data-testid="job-diag-recommended"
          />
          <input
            type="number"
            step="0.01"
            placeholder="Tilleggspris (NOK)"
            className="tk-input max-w-[220px] mb-4"
            value={diag.recommended_price}
            onChange={(e) => setDiag({ ...diag, recommended_price: e.target.value })}
            data-testid="job-diag-price"
          />
          <button onClick={submitDiagnosis} disabled={busy} className="tk-btn-primary !py-2 !px-4 text-sm" data-testid="job-diag-submit">
            {busy ? "Sender …" : "Send diagnose til kunde"}
          </button>
        </div>
      )}

      {/* Status update */}
      {transition && (
        <div className="tk-card flex items-center justify-between" data-testid="job-status-update">
          <div>
            <p className="tk-overline mb-1">Neste steg</p>
            <p className="font-semibold">{transition.label}</p>
            <p className="text-xs text-slate-500 mt-1">
              Trykk for å gå videre. Kunden får automatisk e-post med ny status.
            </p>
          </div>
          <button onClick={() => advance(transition.to)} disabled={busy} className="tk-btn-primary" data-testid={`advance-${transition.to}`}>
            {transition.label} →
          </button>
        </div>
      )}
    </div>
  );
}
